import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { ProgressBar } from '@ets/design-system';

import { useDispatch, useSelector } from '_common/hooks';
import { navigateToMyFiles } from 'router/history';

import { setCurrentAppPage } from 'App/redux/appSlice';
import { searchObjects, setInfoPanelOpen, setLoading } from 'Search/redux/SearchPageSlice';

import { ErrorView, InformationPanel } from '_common/components';

import AdvancedSearchFilters from './AdvancedSearchFilters/AdvancedSearchFilters';
import SearchBreadcrumb from './SearchBreadcrumb/SearchBreadcrumb';

import styles from './SearchPage.module.scss';
import Table from './Table/Table';

export const pageIdentity = 'search';

const SearchPage = () => {
  const dispatch = useDispatch();

  const error = useSelector((state) => state.app.error);

  const parent = useSelector((state) => state.search.parent);
  const initialParent = useSelector((state) => state.search.initialParent);
  const searchLoading = useSelector((state) => state.search.loading);
  const searchQuery = useSelector((state) => state.search.searchQuery);
  const infoPanelOpen = useSelector((state) => state.search.infoPanelOpen);
  const activeTab = useSelector((state) => state.search.activeTab);

  const params = useSelector((state) => state.search.request.params);
  const isNextPageLoading = useSelector((state) => state.search.request.isNextPageLoading);

  useEffect(() => {
    dispatch(setCurrentAppPage('/search'));
  }, []);

  useEffect(() => {
    const promise = triggerSearch();

    return () => {
      promise?.abort?.();
    };
  }, [searchQuery, activeTab, params.order_field, params.order_type]);

  // ------------------------------------------------------
  // ---------------------- Utils -------------------------
  const triggerSearch = (lazyload = false) => {
    if (lazyload && isNextPageLoading) {
      dispatch(setLoading(false));
      return null;
    }

    let tempQuery: string[] = [];
    if (searchQuery) {
      tempQuery.push(searchQuery);
    }
    if (activeTab === 'shared') {
      tempQuery.push(`share = "True"`);
    } else if (activeTab === 'parent' && initialParent) {
      tempQuery.push(`parent = "${initialParent.id}"`);
    }

    return dispatch(
      searchObjects({
        parent,
        query: tempQuery.join(' and '),
        params: { ...params, offset: lazyload ? params.offset : 0, lazyload },
      }),
    );
  };

  const checkErrorStatus = () => {
    return error.status === 400 || error.status === 403 || error.status === 404;
  };

  // ------------------------------------------------------
  // --------------------- Events -------------------------

  const toggleInfoPanel = () => {
    dispatch(setInfoPanelOpen(!infoPanelOpen));
  };

  // ------------------------------------------------------
  // ---------------------- Render ------------------------

  //#endregion

  const renderContent = () => {
    if (searchLoading) {
      return (
        <div className={styles.loading}>
          <ProgressBar variant="primary-dark" testId="search-loading" size="m" />
          <div className={styles.message}>
            <FormattedMessage id="SEARCHING_FOR_RESULTS" />
          </div>
        </div>
      );
    }

    // check for error view
    if (checkErrorStatus()) {
      return <ErrorView error={error} onClick={() => navigateToMyFiles(false)} />;
    }

    return (
      <div className={styles.content} data-testid="search-content">
        <Table searchQuery={searchQuery} />
        <InformationPanel
          isOpen={infoPanelOpen}
          onClose={toggleInfoPanel}
          testId={'search-information-panel'}
        />
      </div>
    );
  };

  return (
    <div className={styles.root}>
      <SearchBreadcrumb />
      <AdvancedSearchFilters />
      {renderContent()}
    </div>
  );
};

export default SearchPage;
